import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
    Scales, Bank, MapPin, MagnifyingGlass, ArrowSquareOut, Clipboard, FileText,
    Plus, Compass, ShieldCheck, Sparkle, PlayCircle, ListNumbers,
} from "@phosphor-icons/react";

export default function HelpLookup() {
    const [tab, setTab] = useState("federal");

    return (
        <div className="space-y-10 max-w-[1080px]" data-testid="help-lookup-page">
            {/* Hero */}
            <header>
                <p className="overline">Tutorial · 5 min read</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight tracking-tight">
                    Looking up federal &amp; state judgments<span className="text-[#2563EB]">.</span>
                </h1>
                <p className="mt-5 text-lg text-slate-700 max-w-2xl">
                    Every dispute starts with a verified record. This guide walks through pulling judgments
                    from <strong>federal courts (CourtListener / RECAP)</strong> and from the <strong>14 supported state portals</strong> — so you can
                    capture, verify, and act on them inside LienLift Direct.
                </p>
                <div className="mt-6 flex gap-3 flex-wrap">
                    <Link to="/cases/new?tour=1">
                        <Button className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-5" data-testid="help-launch-tour">
                            <PlayCircle size={16} weight="fill" className="mr-1.5" />
                            Run the 60-second walkthrough
                        </Button>
                    </Link>
                    <Link to="/cases/new">
                        <Button variant="outline" className="h-11 px-5" data-testid="help-go-newcase">
                            <Plus size={14} className="mr-1.5" />
                            Open a new case
                        </Button>
                    </Link>
                </div>
            </header>

            {/* When to use which */}
            <Card className="p-6 bg-slate-50 border-slate-200" data-testid="help-when-to-use">
                <div className="flex items-start gap-3">
                    <Compass size={22} weight="duotone" className="text-[#2563EB] mt-0.5 shrink-0" />
                    <div>
                        <p className="overline">First — which source do you need?</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                            <div className="rounded-md border border-slate-200 bg-white p-4">
                                <div className="flex items-center gap-2"><Bank size={16} weight="duotone" className="text-[#0F1E3D]" /> <h3 className="font-medium">Use Federal (CourtListener)</h3></div>
                                <p className="text-sm text-slate-600 mt-2">
                                    If your judgment was issued by a U.S. District / Bankruptcy / Appeals court, or
                                    if you don&apos;t yet know which state filed it. Covers all 94 federal districts.
                                </p>
                            </div>
                            <div className="rounded-md border border-slate-200 bg-white p-4">
                                <div className="flex items-center gap-2"><MapPin size={16} weight="duotone" className="text-[#0F1E3D]" /> <h3 className="font-medium">Use State Court</h3></div>
                                <p className="text-sm text-slate-600 mt-2">
                                    For consumer judgments, liens, civil debts, and small-claims matters — these
                                    almost always live in <strong>state</strong> court systems (NC AOC, TX OCA, NY eCourts, etc.).
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Detailed tabs */}
            <section>
                <Tabs value={tab} onValueChange={setTab} data-testid="help-source-tabs">
                    <TabsList>
                        <TabsTrigger value="federal" data-testid="help-tab-federal">Federal (CourtListener)</TabsTrigger>
                        <TabsTrigger value="state" data-testid="help-tab-state">State court (14 states)</TabsTrigger>
                    </TabsList>

                    {/* FEDERAL */}
                    <TabsContent value="federal" className="space-y-6 pt-6" data-testid="help-tab-federal-body">
                        <div className="flex items-center gap-2">
                            <Bank size={22} weight="duotone" className="text-[#2563EB]" />
                            <h2 className="font-serif text-2xl text-[#0F1E3D]">Federal lookup walkthrough</h2>
                        </div>
                        <p className="text-slate-700 max-w-2xl">
                            CourtListener (operated by the non-profit Free Law Project) mirrors PACER &amp;
                            RECAP — every federal docket is searchable by case number, party name, or court.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <StepCard n={1} icon={Plus} title="Open New Case">
                                Sidebar → <strong>New Case</strong>. The lookup panel is the first thing you see.
                            </StepCard>
                            <StepCard n={2} icon={Bank} title="Federal tab">
                                Make sure the <strong>Federal (CourtListener)</strong> tab is active.
                            </StepCard>
                            <StepCard n={3} icon={MagnifyingGlass} title="Search by party or case #">
                                Type a debtor / creditor name, a docket number (e.g. <span className="font-mono">1:23-cv-04567</span>),
                                or both. Narrow with court if needed.
                            </StepCard>
                            <StepCard n={4} icon={FileText} title="Pick the docket">
                                Review the result list. Click <strong>Import</strong> next to the correct docket — we pull case number,
                                court, parties, and key dates automatically.
                            </StepCard>
                            <StepCard n={5} icon={ArrowSquareOut} title="(Optional) Open on CourtListener">
                                Use <strong>View Docket</strong> to open the full docket on CourtListener in a new tab. Helpful when
                                you need filings or judgment amounts that aren&apos;t in the summary.
                            </StepCard>
                            <StepCard n={6} icon={Sparkle} title="Form auto-fills">
                                Title, case number, creditor, court &amp; date populate. You just add the amount owed
                                and any client/notes, then save.
                            </StepCard>
                        </div>

                        <Card className="p-5 bg-blue-50 border-blue-200">
                            <p className="overline">Pro tips</p>
                            <ul className="mt-3 text-sm text-slate-700 space-y-2 list-disc pl-5">
                                <li>If you&apos;re unsure of the case number format, search by party name first — CourtListener normalises whitespace.</li>
                                <li>The <em>docket entries</em> on CourtListener are the PDFs themselves. Use these to confirm the judgment amount before settling.</li>
                                <li>Federal records can take 24–48h to surface after a new filing. If you don&apos;t see it, try again the next day or use state court for state-issued judgments.</li>
                            </ul>
                        </Card>
                    </TabsContent>

                    {/* STATE */}
                    <TabsContent value="state" className="space-y-6 pt-6" data-testid="help-tab-state-body">
                        <div className="flex items-center gap-2">
                            <MapPin size={22} weight="duotone" className="text-[#2563EB]" />
                            <h2 className="font-serif text-2xl text-[#0F1E3D]">State court walkthrough</h2>
                        </div>
                        <p className="text-slate-700 max-w-2xl">
                            Most consumer judgments live in state systems. LienLift Direct connects to <strong>14 state
                            portals</strong> — we deep-link to the official public-records site with your search
                            terms pre-loaded into your clipboard.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <StepCard n={1} icon={Plus} title="Open New Case">
                                Sidebar → <strong>New Case</strong>, switch to the <strong>State court</strong> tab.
                            </StepCard>
                            <StepCard n={2} icon={MapPin} title="Pick your state">
                                Choose from the dropdown (NC, TX, FL, VA, SC, NY, CA, OH, WV, PA, MD, CT, ME, WA).
                                The right form appears underneath — county-aware where applicable.
                            </StepCard>
                            <StepCard n={3} icon={MagnifyingGlass} title="Fill in what you know">
                                Last name &amp; first name work for individuals. For LLCs/corps use the
                                business-name field. A case number alone is also enough.
                            </StepCard>
                            <StepCard n={4} icon={ArrowSquareOut} title="Open the portal">
                                Click <strong>Open [state] portal</strong>. We open the official site in a new tab and
                                copy your search query to the clipboard.
                            </StepCard>
                            <StepCard n={5} icon={Clipboard} title="Paste &amp; search on the portal">
                                On the portal&apos;s search page, paste (Ctrl/Cmd + V) and submit. Browse the
                                results until you find your judgment.
                            </StepCard>
                            <StepCard n={6} icon={ListNumbers} title="Capture details back">
                                Return to LienLift &amp; click <strong>Capture details</strong> in the same panel — type in the
                                case number, court, dates, amount, parties. Click save and the new-case form
                                auto-fills.
                            </StepCard>
                        </div>

                        <Card className="p-5 bg-blue-50 border-blue-200">
                            <p className="overline">State-specific tips</p>
                            <ul className="mt-3 text-sm text-slate-700 space-y-2 list-disc pl-5">
                                <li><strong>NC AOC eCourts</strong>: county-level search is the most reliable; if the debtor moved counties, try the prior one too.</li>
                                <li><strong>Texas Re:SearchTX</strong>: statewide, free, no login. Best for civil &amp; small-claims judgments.</li>
                                <li><strong>California</strong>: 58 counties × 58 portals — pick the county where the case was filed. Use Cook/LA County direct portals when available.</li>
                                <li><strong>New York eCourts</strong>: WebCivil for Supreme/County, eCourts for Family/Criminal. Civil judgments are almost always WebCivil.</li>
                                <li><strong>Florida CCIS</strong>: each clerk has its own search. We deep-link to the statewide aggregator; click through to the county to drill in.</li>
                            </ul>
                        </Card>
                    </TabsContent>
                </Tabs>
            </section>

            {/* What to do next */}
            <section>
                <p className="overline">After you&apos;ve found the record</p>
                <h2 className="font-serif text-3xl mt-2">What LienLift does next.</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <Card className="p-5"><FileText size={22} weight="duotone" className="text-[#2563EB]" />
                        <h3 className="font-medium mt-2">Capture into a case</h3>
                        <p className="text-sm text-slate-600 mt-2">The form auto-fills and you save. The judgment now lives in your dashboard.</p>
                    </Card>
                    <Card className="p-5"><Scales size={22} weight="duotone" className="text-[#2563EB]" />
                        <h3 className="font-medium mt-2">Run interest calc</h3>
                        <p className="text-sm text-slate-600 mt-2">Use the state-by-state statutory interest engine to see what&apos;s really owed today.</p>
                    </Card>
                    <Card className="p-5"><ShieldCheck size={22} weight="duotone" className="text-[#2563EB]" />
                        <h3 className="font-medium mt-2">Draft &amp; send</h3>
                        <p className="text-sm text-slate-600 mt-2">AI workbench drafts dispute, validation, or settlement letters. Send a public offer with built-in Stripe checkout.</p>
                    </Card>
                </div>
            </section>

            {/* FAQ */}
            <section>
                <p className="overline">FAQ</p>
                <h2 className="font-serif text-3xl mt-2">Common questions.</h2>
                <Accordion type="single" collapsible className="mt-5" data-testid="help-faq">
                    <AccordionItem value="q1">
                        <AccordionTrigger>I can&apos;t find my judgment — what do I do?</AccordionTrigger>
                        <AccordionContent className="text-sm text-slate-700 leading-relaxed">
                            First confirm the right venue: most consumer debts are <em>state</em> court, not federal.
                            If you still can&apos;t find it, the judgment may be very recent (1–3 day lag is common) or
                            sealed. You can always create the case manually using <strong>New Case → skip lookup</strong> and
                            type the details yourself.
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="q2">
                        <AccordionTrigger>What if my state isn&apos;t in the list yet?</AccordionTrigger>
                        <AccordionContent className="text-sm text-slate-700 leading-relaxed">
                            We&apos;re adding states every release. In the meantime, Federal/RECAP covers federal
                            judgments nationwide, and any state judgment can still be captured manually.
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="q3">
                        <AccordionTrigger>The portal didn&apos;t open in a new tab — only the clipboard copied.</AccordionTrigger>
                        <AccordionContent className="text-sm text-slate-700 leading-relaxed">
                            Some browsers block popups even on user clicks. After every state search,
                            you&apos;ll see an emerald <strong>&ldquo;Portal ready&rdquo;</strong> card with an explicit <strong>Open portal</strong> link
                            — that one will always work.
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="q4">
                        <AccordionTrigger>Is any of this legal advice?</AccordionTrigger>
                        <AccordionContent className="text-sm text-slate-700 leading-relaxed">
                            No. LienLift Direct is a judgment <em>information &amp; document service</em> — not a law firm.
                            We help you research, calculate, draft and send. You sign, you send, you negotiate, you decide.
                            For legal advice consult a licensed attorney in your state.
                        </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="q5">
                        <AccordionTrigger>How current are the records I pull?</AccordionTrigger>
                        <AccordionContent className="text-sm text-slate-700 leading-relaxed">
                            Federal CourtListener mirrors PACER continuously (≈1–2 day delay).
                            State portals are live — what you see is what their clerk is currently publishing.
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </section>

            {/* Bottom CTA */}
            <Card className="p-8 bg-[#0F1E3D] text-white border-0" data-testid="help-bottom-cta">
                <p className="overline !text-white/60">Ready?</p>
                <h2 className="font-serif text-3xl mt-2 leading-tight">Look up your first judgment.</h2>
                <p className="mt-3 text-white/80 max-w-xl">Free, no credit card. You&apos;ll see results in seconds.</p>
                <div className="mt-5 flex gap-3 flex-wrap">
                    <Link to="/cases/new?tour=1">
                        <Button className="bg-white text-[#0F1E3D] hover:bg-white/90 h-11 px-5" data-testid="help-bottom-tour">
                            <PlayCircle size={16} weight="fill" className="mr-1.5" />
                            Take the tour
                        </Button>
                    </Link>
                    <Link to="/cases/new">
                        <Button variant="ghost" className="text-white hover:bg-white/10 h-11 px-5">
                            Start a case →
                        </Button>
                    </Link>
                </div>
            </Card>
        </div>
    );
}

function StepCard({ n, icon: Icon, title, children }) {
    return (
        <Card className="p-5">
            <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0F1E3D] text-white text-xs font-mono">{n}</span>
                {Icon && <Icon size={18} weight="duotone" className="text-[#2563EB]" />}
            </div>
            <h3 className="font-medium mt-3 text-slate-900">{title}</h3>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{children}</p>
        </Card>
    );
}
